'use client'

import { Slot } from '@radix-ui/react-slot'
import { forwardRef } from 'react'
import type { MouseEvent } from 'react'

import { ANALYTICS_EVENTS, track } from '@podo-app/analytics/react'

import { cn } from '../../lib/util'
import { ButtonProps } from './button.types'

import { buttonStyle, buttonWrapperStyle } from './button.css'

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'large',
      shape = 'square',
      asChild = false,
      children,
      className,
      Icon,
      fullWidth = false,
      style,
      eventParams,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button'
    const Wrapper = 'div'
    const sharedFullWidthClass = fullWidth ? 'w-full min-w-0 flex-1' : undefined

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (eventParams) {
        track(ANALYTICS_EVENTS.BUTTON_CLICKED, { $version: '2', ...eventParams })
      }
      onClick?.(e)
    }

    return (
      <Wrapper
        className={cn(buttonWrapperStyle({ size }), sharedFullWidthClass, className)}
        style={fullWidth ? { width: '100%', minWidth: 0, flex: '1 1 0%' } : undefined}
      >
        <Component
          className={cn(buttonStyle({ variant, size, shape }), sharedFullWidthClass, className)}
          style={
            fullWidth
              ? {
                  width: '100%',
                  minWidth: 0,
                  flex: '1 1 0%',
                  ...style,
                }
              : style
          }
          {...props}
          onClick={handleClick}
          ref={ref}
        >
          {Icon && <Icon />}
          {children}
        </Component>
      </Wrapper>
    )
  },
)

Button.displayName = 'Button'

export default Button
